/**
 * Wave Generator for Siege game
 *
 * Generates progressively harder waves with varied unit compositions.
 */

import { UNIT_TYPES, getUnitTypeIds } from './unit-types.js';

/**
 * Wave configuration presets by difficulty
 */
const DIFFICULTY_SCALING = {
    easy: {
        baseUnits: 3,
        unitsPerWave: 1,
        maxUnits: 15,
        specialChance: 0.1,
        bossWaveInterval: 10
    },
    normal: {
        baseUnits: 4,
        unitsPerWave: 1.5,
        maxUnits: 25,
        specialChance: 0.2,
        bossWaveInterval: 7
    },
    hard: {
        baseUnits: 5,
        unitsPerWave: 2,
        maxUnits: 35,
        specialChance: 0.3,
        bossWaveInterval: 5
    },
    nightmare: {
        baseUnits: 7,
        unitsPerWave: 2.5,
        maxUnits: 50,
        specialChance: 0.4,
        bossWaveInterval: 4
    }
};

/**
 * Get wave configuration
 * @param {number} waveNumber - Wave number (1-indexed)
 * @param {string} difficulty - Difficulty level
 * @returns {Object} Wave configuration
 */
export function getWaveConfig(waveNumber, difficulty = 'normal') {
    const scaling = DIFFICULTY_SCALING[difficulty] || DIFFICULTY_SCALING.normal;

    // Calculate total units for this wave
    const rawUnits = scaling.baseUnits + (waveNumber - 1) * scaling.unitsPerWave;
    const totalUnits = Math.min(Math.round(rawUnits), scaling.maxUnits);

    // Determine if this is a boss wave
    const isBossWave = waveNumber % scaling.bossWaveInterval === 0;

    return {
        waveNumber,
        totalUnits,
        isBossWave,
        specialChance: scaling.specialChance,
        difficulty
    };
}

/**
 * Generate wave composition
 * @param {Object} config - Wave configuration from getWaveConfig
 * @returns {Array<{type: string, count: number}>} Unit specs for the wave
 */
export function generateWave(config) {
    const { waveNumber, totalUnits, isBossWave, specialChance } = config;

    const units = [];
    let remaining = totalUnits;

    // Boss waves always have a boss
    if (isBossWave) {
        units.push({ type: 'boss', count: 1 });
        remaining -= 5; // Boss counts as 5 units
    }

    // Calculate unit distribution based on wave number
    const unitDistribution = getUnitDistribution(waveNumber);

    // Distribute remaining units
    while (remaining > 0) {
        // Pick a unit type based on distribution
        const typeId = pickUnitType(unitDistribution, specialChance);
        const count = Math.min(remaining, Math.ceil(Math.random() * 3) + 1);

        // Find existing entry or create new
        const existing = units.find(u => u.type === typeId);
        if (existing) {
            existing.count += count;
        } else {
            units.push({ type: typeId, count });
        }

        remaining -= count;
    }

    return units;
}

/**
 * Get unit type distribution based on wave number
 * @param {number} waveNumber - Current wave
 * @returns {Object} Distribution weights for each unit type
 */
function getUnitDistribution(waveNumber) {
    // Base distribution - mostly grunts early, more variety later
    const distribution = {
        grunt: 100,
        runner: 0,
        tank: 0,
        splitter: 0,
        healer: 0
    };

    // Wave 2+: Introduce runners
    if (waveNumber >= 2) {
        distribution.runner = 20 + waveNumber * 5;
    }

    // Wave 3+: Introduce tanks
    if (waveNumber >= 3) {
        distribution.tank = 10 + waveNumber * 3;
    }

    // Wave 4+: Introduce splitters
    if (waveNumber >= 4) {
        distribution.splitter = 10 + waveNumber * 2;
    }

    // Wave 5+: Introduce healers
    if (waveNumber >= 5) {
        distribution.healer = 5 + waveNumber * 2;
    }

    // Reduce grunt dominance over time
    distribution.grunt = Math.max(30, 100 - waveNumber * 5);

    return distribution;
}

/**
 * Pick a unit type based on distribution weights
 * @param {Object} distribution - Weight for each type
 * @param {number} specialChance - Chance for special types
 * @returns {string} Selected unit type ID
 */
function pickUnitType(distribution, specialChance) {
    // Calculate total weight
    const total = Object.values(distribution).reduce((sum, w) => sum + w, 0);

    // Random selection
    let random = Math.random() * total;

    for (const [typeId, weight] of Object.entries(distribution)) {
        random -= weight;
        if (random <= 0) {
            return typeId;
        }
    }

    // Fallback to grunt
    return 'grunt';
}

/**
 * Get summary of a wave for display
 * @param {Array} unitSpecs - Unit specs from generateWave
 * @returns {Object} Wave summary
 */
export function getWaveSummary(unitSpecs) {
    const summary = {
        totalUnits: 0,
        hasBoss: false,
        types: {}
    };

    for (const spec of unitSpecs) {
        summary.totalUnits += spec.count;
        summary.types[spec.type] = (summary.types[spec.type] || 0) + spec.count;

        if (spec.type === 'boss') {
            summary.hasBoss = true;
        }
    }

    return summary;
}

/**
 * Preset wave definitions for specific wave numbers (optional)
 * These can override procedural generation for key moments
 */
export const PRESET_WAVES = {
    1: [
        { type: 'grunt', count: 4 }
    ],
    2: [
        { type: 'grunt', count: 5 },
        { type: 'runner', count: 2 }
    ],
    3: [
        { type: 'grunt', count: 4 },
        { type: 'runner', count: 3 },
        { type: 'tank', count: 1 }
    ]
};

/**
 * Get wave composition, using preset if available
 * @param {number} waveNumber - Wave number
 * @param {string} difficulty - Difficulty level
 * @returns {Array} Unit specs
 */
export function getWaveComposition(waveNumber, difficulty = 'normal') {
    // Check for preset
    if (PRESET_WAVES[waveNumber] && difficulty === 'normal') {
        return PRESET_WAVES[waveNumber];
    }

    // Generate procedurally
    const config = getWaveConfig(waveNumber, difficulty);
    return generateWave(config);
}
